import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, ExternalLink, Pencil, Search, Star, Video, X } from 'lucide-react';
import {
  RANK_LABEL,
  api,
  canEdit,
  type AuthUser,
  type MType,
  type Player,
  type Profession,
  type Rank,
  type SnapshotBaseline,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import PlayerDrawer from '@/components/PlayerDrawer';
import { MTypeIcon } from '@/components/MTypeIcon';
import { PlayerLabel } from '@/components/PlayerLabel';
import { PlayerFlagChip } from '@/components/PlayerFlagChip';
import ChatBanMuteIcon from '@/components/ChatBanMuteIcon';
import ImportKillsDialog from '@/components/ImportKillsDialog';
import ImportPowerDialog from '@/components/ImportPowerDialog';

type SortKey =
  | 'name'
  | 'rank'
  | 'profession'
  | 'neverSub'
  | 'totalPower'
  | 'kills'
  | 'mSum'
  | 'm1Power'
  | 'm2Power'
  | 'm3Power';

const RANK_ORDER: Record<Rank, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };
const RANKS: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];
const PROFESSIONS: Profession[] = ['warlord', 'engineer'];
const M_TYPES: MType[] = ['planes', 'rockets', 'tanks'];

const DEFAULT_SINCE_DAYS = 28;
function defaultSince(): string {
  const d = new Date();
  d.setDate(d.getDate() - DEFAULT_SINCE_DAYS);
  return d.toISOString().slice(0, 10);
}

export default function Roster({ user }: { user: AuthUser }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [hideOnLeave, setHideOnLeave] = useState(false);
  const [rankFilter, setRankFilter] = useState<'all' | Rank>('all');
  const [professionFilter, setProfessionFilter] = useState<'all' | 'none' | Profession>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('totalPower');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [since, setSince] = useState<string>(() => defaultSince());
  const [killImportOpen, setKillImportOpen] = useState(false);
  const [powerImportOpen, setPowerImportOpen] = useState(false);
  const editable = canEdit(user.role);

  const query = useQuery({
    queryKey: ['players', { includeInactive }],
    queryFn: () => api.listPlayers({ includeInactive }),
  });

  const baselinesQuery = useQuery({
    queryKey: ['snapshotBaselines', since],
    queryFn: () => api.listSnapshotBaselines(since),
    enabled: Boolean(since),
  });

  const baselineById = useMemo(() => {
    const map = new Map<number, SnapshotBaseline>();
    for (const b of baselinesQuery.data ?? []) map.set(b.playerId, b);
    return map;
  }, [baselinesQuery.data]);

  const players = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (query.data ?? []).filter((p) => {
      if (rankFilter !== 'all' && p.rank !== rankFilter) return false;
      if (professionFilter === 'none' && p.profession != null) return false;
      if (
        professionFilter !== 'all' &&
        professionFilter !== 'none' &&
        p.profession !== professionFilter
      )
        return false;
      if (hideOnLeave && p.currentLeave) return false;
      if (needle) {
        const name = p.name.toLowerCase();
        const dn = p.displayName?.toLowerCase() ?? '';
        const rn = p.realName?.toLowerCase() ?? '';
        if (!name.includes(needle) && !dn.includes(needle) && !rn.includes(needle))
          return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [query.data, rankFilter, professionFilter, hideOnLeave, sortKey, sortDir, search]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'profession' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="space-y-4">
      <RosterTabs />
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <Filter label="Search">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or display name…"
              className="w-52 rounded border border-slate-700 bg-slate-900 py-1 pl-7 pr-7 text-sm placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </Filter>
        <Filter label="Rank">
          <select
            value={rankFilter}
            onChange={(e) => setRankFilter(e.target.value as 'all' | Rank)}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm"
          >
            <option value="all">All ranks</option>
            {RANKS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Filter>
        <Filter label="Profession">
          <select
            value={professionFilter}
            onChange={(e) =>
              setProfessionFilter(e.target.value as 'all' | 'none' | Profession)
            }
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm"
          >
            <option value="all">All professions</option>
            <option value="warlord">Warlord</option>
            <option value="engineer">Engineer</option>
            <option value="none">No profession set</option>
          </select>
        </Filter>
        <Filter label="Growth since">
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm"
          />
        </Filter>
        <Toggle checked={hideOnLeave} onChange={setHideOnLeave} label="Hide on leave" />
        <Toggle checked={includeInactive} onChange={setIncludeInactive} label="Show inactive" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{players.length} players</span>
          {editable && (
            <>
              <button
                onClick={() => setKillImportOpen(true)}
                title="Import kill counts from a screen recording"
                className="inline-flex items-center gap-1.5 rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
              >
                <Video size={14} /> Import kills
              </button>
              <button
                onClick={() => setPowerImportOpen(true)}
                title="Import total power from a screen recording — unknown names can be added as new players"
                className="inline-flex items-center gap-1.5 rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
              >
                <Video size={14} /> Import power
              </button>
              <button
                onClick={() => setSelectedId('new')}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
              >
                Add player
              </button>
            </>
          )}
        </div>
      </div>

      {/* Sort bar (mobile only — on desktop sort comes from headers) */}
      <div className="flex flex-wrap items-center gap-2 md:hidden">
        <span className="text-xs text-slate-500">Sort:</span>
        <select
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(':') as [SortKey, 'asc' | 'desc'];
            setSortKey(k);
            setSortDir(d);
          }}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm"
        >
          <option value="totalPower:desc">Total Power ↓</option>
          <option value="totalPower:asc">Total Power ↑</option>
          <option value="kills:desc">Kills ↓</option>
          <option value="mSum:desc">M-Sum (M1+M2+M3) ↓</option>
          <option value="name:asc">Name A–Z</option>
          <option value="rank:desc">Rank (R5→R1)</option>
          <option value="rank:asc">Rank (R1→R5)</option>
          <option value="neverSub:desc">Never-Sub first</option>
        </select>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-lg border border-slate-800 md:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2 text-right text-slate-500" title="Row number (follows current sort)">#</th>
              <Th onClick={() => toggleSort('neverSub')} active={sortKey === 'neverSub'} dir={sortDir} title="Never-Sub">
                <Star size={14} className="inline" />
              </Th>
              <Th onClick={() => toggleSort('name')} active={sortKey === 'name'} dir={sortDir}>
                Name
              </Th>
              <Th onClick={() => toggleSort('rank')} active={sortKey === 'rank'} dir={sortDir}>
                Rank
              </Th>
              <Th onClick={() => toggleSort('profession')} active={sortKey === 'profession'} dir={sortDir}>
                Prof.
              </Th>
              <Th onClick={() => toggleSort('totalPower')} active={sortKey === 'totalPower'} dir={sortDir}>
                Power
              </Th>
              <Th onClick={() => toggleSort('kills')} active={sortKey === 'kills'} dir={sortDir}>
                Kills
              </Th>
              <Th onClick={() => toggleSort('mSum')} active={sortKey === 'mSum'} dir={sortDir} title="Sum of M1+M2+M3">
                M-Sum
              </Th>
              <Th onClick={() => toggleSort('m1Power')} active={sortKey === 'm1Power'} dir={sortDir}>
                M1
              </Th>
              <Th onClick={() => toggleSort('m2Power')} active={sortKey === 'm2Power'} dir={sortDir}>
                M2
              </Th>
              <Th onClick={() => toggleSort('m3Power')} active={sortKey === 'm3Power'} dir={sortDir}>
                M3
              </Th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">{editable && 'Edit'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {query.isLoading && (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!query.isLoading && players.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-slate-500">
                  No players match the current filters.
                </td>
              </tr>
            )}
            {players.map((p, i) =>
              editingId === p.id ? (
                <EditRow
                  key={p.id}
                  player={p}
                  position={i + 1}
                  onCancel={() => setEditingId(null)}
                  onSaved={() => setEditingId(null)}
                />
              ) : (
                <ReadRow
                  key={p.id}
                  player={p}
                  position={i + 1}
                  editable={editable}
                  baseline={baselineById.get(p.id) ?? null}
                  since={since}
                  onOpen={() => setSelectedId(p.id)}
                  onEdit={() => setEditingId(p.id)}
                />
              ),
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {query.isLoading && <div className="text-center text-slate-500">Loading…</div>}
        {!query.isLoading && players.length === 0 && (
          <div className="text-center text-slate-500">No players match the current filters.</div>
        )}
        {players.map((p, i) => (
          <PlayerCard
            key={p.id}
            player={p}
            position={i + 1}
            editable={editable}
            onOpen={() => setSelectedId(p.id)}
            isEditing={editingId === p.id}
            onEdit={() => setEditingId(p.id)}
            onCancelEdit={() => setEditingId(null)}
            onSaved={() => setEditingId(null)}
          />
        ))}
      </div>

      {selectedId !== null && (
        <PlayerDrawer
          playerId={selectedId}
          user={user}
          onClose={() => setSelectedId(null)}
        />
      )}

      {editable && (
        <ImportKillsDialog
          open={killImportOpen}
          onClose={() => setKillImportOpen(false)}
          onImported={() => {
            query.refetch();
            baselinesQuery.refetch();
          }}
        />
      )}
      {editable && (
        <ImportPowerDialog
          open={powerImportOpen}
          onClose={() => setPowerImportOpen(false)}
          onImported={() => {
            query.refetch();
            baselinesQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function leaveTitle(leave: NonNullable<Player['currentLeave']>): string {
  const parts = [`On leave since ${leave.startsAt}`];
  if (leave.endsAt) parts.push(`until ${leave.endsAt}`);
  if (leave.reason) parts.push(`— ${leave.reason}`);
  return parts.join(' ');
}

function mSum(p: Player): number {
  const s = p.latestSnapshot;
  if (!s) return 0;
  return s.m1Power + s.m2Power + s.m3Power;
}

function compare(a: Player, b: Player, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'rank':
      return RANK_ORDER[a.rank] - RANK_ORDER[b.rank];
    case 'profession':
      return (a.profession ?? 'zzz').localeCompare(b.profession ?? 'zzz');
    case 'neverSub':
      return Number(a.neverSub) - Number(b.neverSub);
    case 'mSum':
      return mSum(a) - mSum(b);
    default:
      return (a.latestSnapshot?.[key] ?? 0) - (b.latestSnapshot?.[key] ?? 0);
  }
}

function NeverSubToggle({ player, disabled }: { player: Player; disabled?: boolean }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.updatePlayer(player.id, { neverSub: !player.neverSub }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['players'] }),
  });
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) mutation.mutate();
      }}
      disabled={disabled || mutation.isPending}
      title={player.neverSub ? 'Never-Sub — click to unset' : 'Mark as Never-Sub'}
      className={cn(
        'rounded p-1 transition',
        player.neverSub ? 'text-amber-400' : 'text-slate-700 hover:text-slate-400',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Star size={16} fill={player.neverSub ? 'currentColor' : 'none'} />
    </button>
  );
}

function ReadRow({
  player,
  position,
  editable,
  baseline,
  since,
  onOpen,
  onEdit,
}: {
  player: Player;
  position: number | null;
  editable: boolean;
  baseline: SnapshotBaseline | null;
  since: string;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const s = player.latestSnapshot;
  const currentDate = s?.recordedAt.slice(0, 10);
  const rangeTitle = baseline && currentDate
    ? `${baseline.recordedAt.slice(0, 10)} → ${currentDate}`
    : since
      ? `No snapshot on or before ${since}`
      : '';
  return (
    <tr className="cursor-pointer hover:bg-slate-900/60" onClick={onOpen}>
      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{position ?? '—'}</td>
      <td className="px-3 py-2">
        <NeverSubToggle player={player} disabled={!editable} />
      </td>
      <td className="px-3 py-2 font-medium">
        <div className="flex items-center gap-1.5">
          <PlayerLabel player={player} showStar={false} />
          <ChatBanMuteIcon playerId={player.id} />
          {player.currentLeave && (
            <span
              className="text-sm leading-none"
              title={leaveTitle(player.currentLeave)}
              aria-label="On leave"
            >
              🌴
            </span>
          )}
          <span onClick={(e) => e.stopPropagation()}>
            <PlayerFlagChip
              playerId={player.id}
              flags={player.activeFlags ?? []}
              editable={editable}
            />
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-slate-300">{player.rank}</td>
      <td className="px-3 py-2 capitalize text-slate-300">
        {player.profession ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2 tabular-nums">{formatPower(s?.totalPower)}</td>
      <td className="px-3 py-2 tabular-nums">
        <span className="inline-flex items-center gap-1.5">
          {formatPower(s?.kills)}
          <DeltaChip
            current={s?.kills ?? null}
            base={baseline?.kills ?? null}
            title={rangeTitle}
          />
        </span>
      </td>
      <td className="px-3 py-2 tabular-nums text-slate-300">
        <span className="inline-flex items-center gap-1.5">
          {s ? formatPower(mSum(player)) : '—'}
          <DeltaChip
            current={s ? mSum(player) : null}
            base={baseline ? baseline.m1Power + baseline.m2Power + baseline.m3Power : null}
            title={rangeTitle}
          />
        </span>
      </td>
      <MCell power={s?.m1Power} type={s?.m1Type} base={baseline?.m1Power} title={rangeTitle} />
      <MCell power={s?.m2Power} type={s?.m2Type} base={baseline?.m2Power} title={rangeTitle} />
      <MCell power={s?.m3Power} type={s?.m3Type} base={baseline?.m3Power} title={rangeTitle} />
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {!player.active && (
            <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-200">Inactive</span>
          )}
          {player.currentLeave && (
            <span className="rounded bg-amber-700/70 px-2 py-0.5 text-xs text-amber-50">
              On leave
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {editable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="rounded border border-slate-700 p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            title="Inline edit"
          >
            <Pencil size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

function MCell({
  power,
  type,
  base,
  title,
}: {
  power?: number | null;
  type?: MType | null;
  base?: number | null;
  title?: string;
}) {
  if (power == null) return <td className="px-3 py-2 text-slate-600">—</td>;
  return (
    <td className="px-3 py-2 tabular-nums">
      <span className="inline-flex items-center gap-1.5">
        <MTypeIcon type={type} />
        {formatPower(power)}
        <DeltaChip current={power} base={base ?? null} title={title} />
      </span>
    </td>
  );
}

function DeltaChip({
  current,
  base,
  title,
}: {
  current: number | null;
  base: number | null;
  title?: string;
}) {
  if (current == null || base == null) return null;
  const diff = current - base;
  if (Math.abs(diff) < 0.05) return null;
  const positive = diff > 0;
  const sign = positive ? '+' : '−';
  return (
    <span
      title={title}
      className={cn(
        'inline-flex select-none items-center rounded px-1 py-0.5 text-[10px] font-medium tabular-nums',
        positive
          ? 'bg-emerald-900/40 text-emerald-300'
          : 'bg-red-900/40 text-red-300',
      )}
    >
      {sign}
      {Math.abs(diff).toFixed(1)}
    </span>
  );
}

type EditableState = {
  name: string;
  rank: Rank;
  profession: Profession | '';
  totalPower: string;
  kills: string;
  m1Power: string;
  m1Type: MType;
  m2Power: string;
  m2Type: MType;
  m3Power: string;
  m3Type: MType;
};

function snapshotInitial(player: Player): EditableState {
  const s = player.latestSnapshot;
  return {
    name: player.name,
    rank: player.rank,
    profession: player.profession ?? '',
    totalPower: s ? String(s.totalPower) : '',
    kills: s ? String(s.kills) : '',
    m1Power: s ? String(s.m1Power) : '',
    m1Type: s?.m1Type ?? 'planes',
    m2Power: s ? String(s.m2Power) : '',
    m2Type: s?.m2Type ?? 'rockets',
    m3Power: s ? String(s.m3Power) : '',
    m3Type: s?.m3Type ?? 'tanks',
  };
}

function useEditSave(player: Player, onSaved: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (state: EditableState) => {
      const playerChanged =
        state.name !== player.name ||
        state.rank !== player.rank ||
        (state.profession || null) !== (player.profession ?? null);
      if (playerChanged) {
        await api.updatePlayer(player.id, {
          name: state.name,
          rank: state.rank,
          profession: state.profession || null,
        });
      }
      const s = player.latestSnapshot;
      const fields: ['totalPower', 'kills', 'm1Power', 'm2Power', 'm3Power'] = [
        'totalPower',
        'kills',
        'm1Power',
        'm2Power',
        'm3Power',
      ];
      const typeFields: ['m1Type', 'm2Type', 'm3Type'] = ['m1Type', 'm2Type', 'm3Type'];
      const snapshotChanged =
        !s ||
        fields.some((k) => Number(state[k]) !== s[k]) ||
        typeFields.some((k) => state[k] !== s[k]);
      if (snapshotChanged) {
        await api.addSnapshot(player.id, {
          totalPower: Number(state.totalPower),
          kills: Number(state.kills),
          m1Power: Number(state.m1Power),
          m1Type: state.m1Type,
          m2Power: Number(state.m2Power),
          m2Type: state.m2Type,
          m3Power: Number(state.m3Power),
          m3Type: state.m3Type,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      qc.invalidateQueries({ queryKey: ['snapshots', player.id] });
      onSaved();
    },
  });
}

function EditRow({
  player,
  position,
  onCancel,
  onSaved,
}: {
  player: Player;
  position: number | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [state, setState] = useState<EditableState>(() => snapshotInitial(player));
  const save = useEditSave(player, onSaved);

  return (
    <tr className="bg-slate-900/60">
      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{position ?? '—'}</td>
      <td className="px-3 py-2">
        <NeverSubToggle player={player} />
      </td>
      <td className="px-2 py-1.5">
        <input
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          className="edit-input w-32"
        />
      </td>
      <td className="px-2 py-1.5">
        <select
          value={state.rank}
          onChange={(e) => setState({ ...state, rank: e.target.value as Rank })}
          className="edit-input"
        >
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={state.profession}
          onChange={(e) => setState({ ...state, profession: e.target.value as Profession | '' })}
          className="edit-input"
        >
          <option value="">—</option>
          {PROFESSIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <NumberInput
          value={state.totalPower}
          onChange={(v) => setState({ ...state, totalPower: v })}
        />
      </td>
      <td className="px-2 py-1.5">
        <NumberInput value={state.kills} onChange={(v) => setState({ ...state, kills: v })} />
      </td>
      <MEdit slot="m1" state={state} setState={setState} />
      <MEdit slot="m2" state={state} setState={setState} />
      <MEdit slot="m3" state={state} setState={setState} />
      <td className="px-2 py-1.5">
        <div className="flex flex-wrap gap-1">
          {!player.active && (
            <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-200">Inactive</span>
          )}
          {player.currentLeave && (
            <span className="rounded bg-amber-700/70 px-2 py-0.5 text-xs text-amber-50">
              On leave
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => save.mutate(state)}
            disabled={save.isPending}
            className="rounded bg-emerald-600 p-1.5 text-white hover:bg-emerald-500 disabled:opacity-50"
            title="Save"
          >
            <Check size={14} />
          </button>
          <button
            onClick={onCancel}
            className="rounded border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800"
            title="Cancel"
          >
            <X size={14} />
          </button>
        </div>
      </td>
      <EditStyle />
    </tr>
  );
}

function MEdit({
  slot,
  state,
  setState,
}: {
  slot: 'm1' | 'm2' | 'm3';
  state: EditableState;
  setState: (s: EditableState) => void;
}) {
  const powerKey = `${slot}Power` as const;
  const typeKey = `${slot}Type` as const;
  return (
    <td className="px-2 py-1.5">
      <div className="flex items-center gap-1">
        <NumberInput
          value={state[powerKey]}
          onChange={(v) => setState({ ...state, [powerKey]: v } as EditableState)}
          className="w-16"
        />
        <select
          value={state[typeKey]}
          onChange={(e) =>
            setState({ ...state, [typeKey]: e.target.value as MType } as EditableState)
          }
          className="edit-input"
        >
          {M_TYPES.map((t) => (
            <option key={t} value={t}>
              {t[0].toUpperCase() + t.slice(1, 2)}
            </option>
          ))}
        </select>
      </div>
    </td>
  );
}

function NumberInput({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <input
      type="number"
      step="0.1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn('edit-input w-20 tabular-nums', className)}
    />
  );
}

function EditStyle() {
  return (
    <style>{`
      .edit-input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 4px;
        padding: 4px 6px;
        font-size: 13px;
        color: inherit;
        max-width: 100%;
      }
      .edit-input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}

function PlayerCard({
  player,
  position,
  editable,
  isEditing,
  onOpen,
  onEdit,
  onCancelEdit,
  onSaved,
}: {
  player: Player;
  position: number | null;
  editable: boolean;
  isEditing: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaved: () => void;
}) {
  const s = player.latestSnapshot;
  const [state, setState] = useState<EditableState>(() => snapshotInitial(player));
  const save = useEditSave(player, onSaved);

  if (isEditing) {
    return (
      <div className="rounded-lg border border-indigo-700 bg-slate-900 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <input
            value={state.name}
            onChange={(e) => setState({ ...state, name: e.target.value })}
            className="edit-input flex-1 text-base font-medium"
          />
          <NeverSubToggle player={player} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={state.rank}
            onChange={(e) => setState({ ...state, rank: e.target.value as Rank })}
            className="edit-input"
          >
            {RANKS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={state.profession}
            onChange={(e) => setState({ ...state, profession: e.target.value as Profession | '' })}
            className="edit-input"
          >
            <option value="">— No profession —</option>
            {PROFESSIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <LabeledNumber
            label="Total"
            value={state.totalPower}
            onChange={(v) => setState({ ...state, totalPower: v })}
          />
          <LabeledNumber
            label="Kills"
            value={state.kills}
            onChange={(v) => setState({ ...state, kills: v })}
          />
        </div>
        {(['m1', 'm2', 'm3'] as const).map((slot) => (
          <div key={slot} className="grid grid-cols-[1fr,auto] gap-2">
            <LabeledNumber
              label={slot.toUpperCase()}
              value={state[`${slot}Power`]}
              onChange={(v) => setState({ ...state, [`${slot}Power`]: v } as EditableState)}
            />
            <select
              value={state[`${slot}Type`]}
              onChange={(e) =>
                setState({ ...state, [`${slot}Type`]: e.target.value as MType } as EditableState)
              }
              className="edit-input self-end"
            >
              {M_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => save.mutate(state)}
            disabled={save.isPending}
            className="flex-1 rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={onCancelEdit}
            className="rounded border border-slate-700 px-3 py-2 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
        <EditStyle />
      </div>
    );
  }

  return <PlayerCardDisplay
    player={player}
    position={position}
    editable={editable}
    snapshot={s}
    onOpen={onOpen}
    onEdit={onEdit}
  />;
}

function PlayerCardDisplay({
  player,
  position,
  editable,
  snapshot,
  onOpen,
  onEdit,
}: {
  player: Player;
  position: number | null;
  editable: boolean;
  snapshot: Player['latestSnapshot'];
  onOpen: () => void;
  onEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const s = snapshot;
  return (
    <div
      onClick={() => setExpanded((v) => !v)}
      className="cursor-pointer rounded-lg border border-slate-800 bg-slate-900/40 p-2 transition hover:bg-slate-900"
    >
      <div className="flex items-center gap-2">
        <span className="w-6 text-right text-xs tabular-nums text-slate-500">{position ?? '—'}</span>
        <NeverSubToggle player={player} disabled={!editable} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <PlayerLabel player={player} showStar={false} nameClassName="font-medium" />
            <ChatBanMuteIcon playerId={player.id} />
            {player.currentLeave && (
              <span
                className="text-sm leading-none"
                title={leaveTitle(player.currentLeave)}
                aria-label="On leave"
              >
                🌴
              </span>
            )}
            <span onClick={(e) => e.stopPropagation()}>
              <PlayerFlagChip
                playerId={player.id}
                flags={player.activeFlags ?? []}
                editable={editable}
              />
            </span>
          </div>
          <div className="text-xs text-slate-400">
            {player.rank}
            {player.profession && (
              <>
                {' · '}
                <span className="capitalize">{player.profession}</span>
              </>
            )}
            {!player.active && (
              <span className="ml-2 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] text-slate-200">
                Inactive
              </span>
            )}
            {player.currentLeave && (
              <span className="ml-2 rounded bg-amber-700/70 px-1.5 py-0.5 text-[10px] text-amber-50">
                On leave
              </span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="rounded border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
          title="Open details"
        >
          <ExternalLink size={14} />
        </button>
        {editable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="rounded border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
            title="Inline edit"
          >
            <Pencil size={14} />
          </button>
        )}
        <span className="text-slate-500">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </div>
      {expanded && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <Stat label="Power" value={formatPower(s?.totalPower)} />
          <Stat label="Kills" value={formatPower(s?.kills)} />
          <Stat label="M-Sum" value={s ? formatPower(mSum(player)) : '—'} />
          {(['m1', 'm2', 'm3'] as const).map((slot) => (
            <Stat
              key={slot}
              label={slot.toUpperCase()}
              value={
                <span className="inline-flex items-center gap-1.5">
                  <MTypeIcon type={s?.[`${slot}Type`]} />
                  {formatPower(s?.[`${slot}Power`])}
                </span>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded bg-slate-950/60 px-2 py-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="tabular-nums">{value}</div>
    </div>
  );
}

function LabeledNumber({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="text-xs text-slate-400">
      <span>{label}</span>
      <input
        type="number"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="edit-input w-full mt-1 tabular-nums"
      />
    </label>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-400">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-700 bg-slate-900"
      />
      {label}
    </label>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: 'asc' | 'desc';
  title?: string;
}) {
  return (
    <th
      onClick={onClick}
      title={title}
      className={cn('cursor-pointer select-none px-3 py-2', active && 'text-white')}
    >
      {children}
      {active && <span className="ml-1 text-xs">{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

export function RosterTabs() {
  // Same cache entry App.tsx fills — no extra request.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60 * 1000 });
  const role = meQuery.data?.role;
  const tabs = [
    { to: "/roster", label: "Roster", end: true },
    { to: "/roster/self-reports", label: "Self-Reports", end: false },
    { to: "/roster/access", label: "Player access", end: false },
    { to: "/roster/chat-bans", label: "Chat bans", end: false },
    { to: "/strength-requests", label: "Strength Requests", end: false },
    ...(role && canEdit(role)
      ? [
          { to: "/roster/kill-growth", label: "Kill growth", end: false },
          { to: "/roster/insights", label: "Insights", end: false },
        ]
      : []),
  ];
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-slate-800 bg-slate-900 p-0.5 text-sm">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              "whitespace-nowrap rounded px-3 py-1.5 transition",
              isActive ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white",
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}
